import { Spacer, Spinner } from "@hackthenorth/north";
import { TextInput, TextArea, Checkbox, InlineError, Button } from "north.js";
import React from "react";
import styled from "styled-components";

import {
  Dropzone,
  FormFieldLabelRow,
  OptionalLabel,
} from "src/shared/components";
import { uploadHeadshotImg } from "src/static/img";
import { useFileDropzone } from "src/views/hacker/rsvp/career/useFileDropzone";

import {
  NAME_MAX_CHARS,
  BIO_MAX_CHARS,
  SKILL_MAX_CHARS,
  NUM_SKILL_INPUTS,
  HEADSHOT_FILE_TYPES,
  PRONOUN_OPTIONS,
  OTHER_PRONOUN_OPTION,
  ICEBREAKER_PROMPTS,
  getCoreFieldsPublishErrors,
} from "../constants";
import { normalizeProfilePhoto } from "../normalizeProfilePhoto";
import ProfileDropdownMenu from "../ProfileDropdownMenu";
import {
  captionTextStyles,
  fieldLabelStyles,
  subtitleTextStyles,
  textAreaStyles,
} from "../styles";
import { ProfileFormValues } from "../types";

export interface CoreFieldsProps {
  staging: ProfileFormValues;
  setStagingField: <K extends keyof ProfileFormValues>(
    field: K,
    value: ProfileFormValues[K]
  ) => void;
  /** When true, publish-blocking validation errors are shown inline. */
  showErrors: boolean;
}

const CoreFields: React.FC<CoreFieldsProps> = ({
  staging,
  setStagingField,
  showErrors,
}) => {
  const errors = getCoreFieldsPublishErrors(staging);

  const {
    dropzoneInputRef,
    fetchedFile,
    loading: isFileLoading,
    error: fileError,
    addFile,
    onFileError,
    setDropzoneErrorMessage,
  } = useFileDropzone(
    staging.profile_photo?.[0],
    (files) => setStagingField("profile_photo", files ?? null),
    HEADSHOT_FILE_TYPES,
    undefined,
    normalizeProfilePhoto
  );

  const pronouns = staging.pronouns;
  const hasOtherPronoun = !!pronouns?.[0]?.includes(OTHER_PRONOUN_OPTION);
  const otherPronounValue =
    pronouns?.[0]?.substring(OTHER_PRONOUN_OPTION.length) ?? "";
  const bioPrompt = staging.bio_prompt ?? "";
  const bioPromptError = showErrors ? errors.bioPrompt : undefined;

  const setSkill = (index: number, value: string) => {
    const next = Array.from(
      { length: NUM_SKILL_INPUTS },
      (_, i) => staging.skills?.[i] ?? ""
    );
    next[index] = value;
    setStagingField("skills", next);
  };

  return (
    <>
      <Heading>General</Heading>

      <QuestionWrapper>
        <FieldLabel>Display name</FieldLabel>
        <Spacer height={12} />
        <TextInputStyled
          placeholder="Enter your display name"
          maxLength={NAME_MAX_CHARS}
          value={staging.name ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setStagingField("name", e.target.value)
          }
          error={showErrors && errors.name}
        />
        <HintText>
          This will be the name displayed on your hacker profile, and can be
          distinct from your legal or account display name!
        </HintText>
      </QuestionWrapper>

      <QuestionWrapper>
        <FormFieldLabelRow>
          <FieldLabel>Preferred pronouns</FieldLabel>
          <OptionalLabel>Optional</OptionalLabel>
        </FormFieldLabelRow>
        <Spacer height={12} />
        {PRONOUN_OPTIONS.map((option, index) => {
          const isOtherOption = index === PRONOUN_OPTIONS.length - 1;
          return (
            <CheckboxStyled
              key={index}
              label={option}
              checked={
                isOtherOption
                  ? hasOtherPronoun
                  : (pronouns || []).includes(option)
              }
              value={option}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (isOtherOption && e.target.checked) {
                  setStagingField("pronouns", [
                    OTHER_PRONOUN_OPTION,
                    ...(pronouns || []),
                  ]);
                } else if (isOtherOption) {
                  setStagingField("pronouns", (pronouns || [""]).slice(1));
                } else if (e.target.checked) {
                  setStagingField("pronouns", [...(pronouns || []), option]);
                } else {
                  setStagingField(
                    "pronouns",
                    (pronouns || []).filter((p) => p !== option)
                  );
                }
              }}
            />
          );
        })}
        {hasOtherPronoun && (
          <CheckboxTextInputWrapper>
            <TextInputStyled
              placeholder="Please enter pronouns"
              value={otherPronounValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setStagingField("pronouns", [
                  OTHER_PRONOUN_OPTION + e.target.value,
                  ...(pronouns || [""]).slice(1),
                ])
              }
            />
            <HintText>Separate sets of pronouns by a comma.</HintText>
          </CheckboxTextInputWrapper>
        )}
      </QuestionWrapper>

      <QuestionWrapper>
        <FieldLabel>Upload your headshot</FieldLabel>
        <Spacer height={4} />
        <HelperText>Show off your personality with your headshot!</HelperText>
        <Spacer height={12} />
        {/* Keep Dropzone mounted so Replace can reopen the same file input */}
        <DropzoneContainer isHidden={!!fetchedFile && !isFileLoading}>
          <Dropzone
            max={1}
            icon={<UploadHeadshotIcon src={uploadHeadshotImg} alt="" />}
            label={
              <span>
                <UploadFileText>Upload a file</UploadFileText> or drag and drop
              </span>
            }
            acceptedFileTypes={HEADSHOT_FILE_TYPES}
            fileTypeLabel="PNG, JPG, JPEG"
            onFilesUpload={addFile}
            onError={onFileError}
            ref={dropzoneInputRef}
            onBlur={() => setDropzoneErrorMessage("")}
          />
        </DropzoneContainer>
        {isFileLoading && (
          <>
            <Spacer height={8} />
            <Spinner />
          </>
        )}
        {!isFileLoading && fetchedFile?.uri && (
          <HeadshotPreview>
            <PreviewImage
              src={fetchedFile.uri}
              alt={fetchedFile.name ?? "Headshot preview"}
            />
            <PreviewFileName>{fetchedFile.name}</PreviewFileName>
            <ReplaceButton
              color="secondary"
              size="sm"
              type="button"
              onClick={() => dropzoneInputRef.current?.click()}
            >
              Replace
            </ReplaceButton>
          </HeadshotPreview>
        )}
        {(fileError || (showErrors && errors.profilePhoto)) && (
          <>
            <Spacer height={4} />
            <InlineError>{fileError || errors.profilePhoto}</InlineError>
          </>
        )}
      </QuestionWrapper>

      <QuestionWrapper>
        <FieldLabel>Break the ice!</FieldLabel>
        <Spacer height={4} />
        <HelperText>
          Pick a prompt to show future teammates your vibe, building style, or
          hacking philosophy.
        </HelperText>
        <Spacer height={12} />
        {/* Flex wrapper stretches the dropdown's inline-block root to full width */}
        <PromptDropdownWrapper>
          <ProfileDropdownMenu
            items={[
              ICEBREAKER_PROMPTS.map((prompt) => ({
                content: <PromptItemText>{prompt}</PromptItemText>,
                selected: prompt === bioPrompt,
                action: () => setStagingField("bio_prompt", prompt),
              })),
            ]}
            hasError={!!bioPromptError}
            describedBy={bioPromptError ? "bio-prompt-error" : undefined}
          >
            <PromptTriggerText isPlaceholder={!bioPrompt}>
              {bioPrompt || "Select a prompt"}
            </PromptTriggerText>
            <ChevronDown />
          </ProfileDropdownMenu>
        </PromptDropdownWrapper>
        {bioPromptError && (
          <>
            <Spacer height={4} />
            <InlineError id="bio-prompt-error">{bioPromptError}</InlineError>
          </>
        )}
        {bioPrompt ? (
          <>
            <Spacer height={12} />
            <TextAreaStyled
              placeholder="Start typing here!"
              rows={4}
              fullWidth
              maxLength={BIO_MAX_CHARS}
              value={staging.bio ?? ""}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setStagingField("bio", e.target.value)
              }
              error={showErrors && errors.bio}
            />
            <CharCount>
              {(staging.bio ?? "").length}/{BIO_MAX_CHARS} chars
            </CharCount>
          </>
        ) : null}
      </QuestionWrapper>

      <QuestionWrapper>
        <FieldLabel>What are your strongest skills?</FieldLabel>
        <Spacer height={4} />
        <HelperText>
          List up to {NUM_SKILL_INPUTS} skills that show off your technical or
          creative skills!
        </HelperText>
        <Spacer height={12} />
        {Array.from({ length: NUM_SKILL_INPUTS }, (_, index) => (
          <SkillWrapper key={index}>
            <FormFieldLabelRow>
              <SkillLabel>Skill {index + 1}</SkillLabel>
              <OptionalLabel>Optional</OptionalLabel>
            </FormFieldLabelRow>
            <Spacer height={8} />
            <TextInputStyled
              name={`directory-profile-competency-${index + 1}`}
              type="text"
              inputMode="text"
              autoComplete="off"
              aria-label={`Skill ${index + 1}`}
              placeholder="Describe your skill"
              maxLength={SKILL_MAX_CHARS}
              value={staging.skills?.[index] ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSkill(index, e.target.value)
              }
            />
            <CharCount>
              {(staging.skills?.[index] ?? "").length}/{SKILL_MAX_CHARS} chars
            </CharCount>
          </SkillWrapper>
        ))}
        {showErrors && errors.skills && (
          <InlineError>{errors.skills}</InlineError>
        )}
      </QuestionWrapper>
    </>
  );
};

export default CoreFields;

const Heading = styled.h3`
  font-size: 20px;
  font-weight: 900;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  line-height: 1.5;
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  margin-bottom: 24px;
`;

const QuestionWrapper = styled.div`
  width: 100%;
  margin-bottom: 36px;
`;

const FieldLabel = styled.p`
  ${fieldLabelStyles}
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  margin: 0;
`;

const HelperText = styled.p`
  ${subtitleTextStyles}
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  margin: 0;
`;

const SkillWrapper = styled.div`
  margin-bottom: 16px;
`;

const CheckboxTextInputWrapper = styled.div`
  margin-top: 8px;
  margin-left: 24px;
`;

const TextInputStyled = styled(TextInput).attrs({ size: "md" })`
  font-family: ${({ theme }) =>
    theme.globalConstants.fontFamily.body} !important;
  font-weight: 400 !important;
  font-size: 16px !important;
  line-height: 150% !important;
  letter-spacing: 0 !important;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) =>
      theme.globalConstants.color.textTertiary} !important;
  }
`;

const TextAreaStyled = styled(TextArea)`
  ${textAreaStyles}
`;

const PromptDropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const PromptTriggerText = styled.span<{ isPlaceholder: boolean }>`
  ${subtitleTextStyles}
  color: ${({ theme, isPlaceholder }) =>
    isPlaceholder
      ? theme.globalConstants.color.textTertiary
      : theme.globalConstants.color.textBody};
`;

const SkillLabel = styled.span`
  ${subtitleTextStyles}
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
`;

const UploadFileText = styled.b`
  :hover {
    text-decoration: underline;
  }
`;

const UploadHeadshotIcon = styled.img`
  width: 101px;
`;

const DropzoneContainer = styled.div<{ isHidden: boolean }>`
  display: ${({ isHidden }) => (isHidden ? "none" : "block")};
`;

const HeadshotPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const PreviewImage = styled.img`
  width: 180px;
  height: 180px;
  object-fit: cover;
  border-radius: 4px;
  display: block;
`;

const PreviewFileName = styled.p`
  ${subtitleTextStyles}
  margin: 0;
  font-style: italic;
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
`;

const ReplaceButton = styled(Button)`
  min-width: 96px;
`;

const PromptItemText = styled.span`
  ${subtitleTextStyles}
`;

const ChevronDownIcon = styled.svg`
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  flex-shrink: 0;
`;

const ChevronDown = () => (
  <ChevronDownIcon
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    width={16}
    height={16}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
    />
  </ChevronDownIcon>
);

const CheckboxStyled = styled(Checkbox)`
  ${subtitleTextStyles}
`;

const HintText = styled.p`
  ${captionTextStyles}
  margin: 4px 0 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const CharCount = styled(HintText)`
  text-align: right;
`;
